"use client";

import { Button } from "../primitives/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../primitives/select";
import { cn } from "../primitives/cn";

export type ListPaginationProps = {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  /** Cursor mode: when true, `hasNext` drives Next; total may be unknown (-1). */
  mode?: "page" | "cursor";
  hasNext?: boolean;
  onNext?: () => void;
  onPrev?: () => void;
  labels?: {
    prev?: string;
    next?: string;
    summary?: string;
    rowsPerPage?: string;
  };
  className?: string;
  /** Render inside table footer (no top padding). */
  variant?: "standalone" | "footer";
};

function pageRange(page: number, totalPages: number): (number | "ellipsis")[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const pages: (number | "ellipsis")[] = [1];
  if (page > 3) pages.push("ellipsis");
  const start = Math.max(2, page - 1);
  const end = Math.min(totalPages - 1, page + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (page < totalPages - 2) pages.push("ellipsis");
  pages.push(totalPages);
  return pages;
}

/**
 * External list pagination control for DataTable pages.
 */
export function ListPagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50],
  mode = "page",
  hasNext,
  onNext,
  onPrev,
  labels,
  className,
  variant = "standalone",
}: ListPaginationProps) {
  const prevLabel = labels?.prev ?? "Previous";
  const nextLabel = labels?.next ?? "Next";
  const rowsPerPageLabel = labels?.rowsPerPage ?? "Rows per page";

  const wrapperClass = cn(
    "flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between",
    variant === "standalone" ? "pt-2" : "border-t border-border px-4 py-3",
    className,
  );

  if (mode === "cursor") {
    const canPrev = page > 1;
    const canNext = Boolean(hasNext);
    if (!canPrev && !canNext && total >= 0 && total < 10) return null;
    return (
      <div className={wrapperClass}>
        <p className="text-sm text-muted-foreground">
          {labels?.summary ?? `Page ${page}`}
        </p>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canPrev}
            onClick={() => (onPrev ? onPrev() : onPageChange(page - 1))}
          >
            {prevLabel}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canNext}
            onClick={() => (onNext ? onNext() : onPageChange(page + 1))}
          >
            {nextLabel}
          </Button>
        </div>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  if (total < 10) return null;

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  const summary =
    labels?.summary ?? `Showing ${start}–${end} of ${total} entries`;

  return (
    <div className={wrapperClass}>
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-muted-foreground">{summary}</p>
        {onPageSizeChange ? (
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            {rowsPerPageLabel}
            <Select
              value={String(pageSize)}
              onValueChange={(v) => onPageSizeChange(Number(v))}
            >
              <SelectTrigger size="compact" className="w-[4.5rem]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        ) : null}
      </div>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          aria-label={prevLabel}
        >
          {prevLabel}
        </Button>
        {pageRange(page, totalPages).map((p, idx) =>
          p === "ellipsis" ? (
            <span
              key={`ellipsis-${idx}`}
              className="px-1 text-sm text-muted-foreground"
            >
              …
            </span>
          ) : (
            <Button
              key={p}
              type="button"
              variant={p === page ? "default" : "outline"}
              size="sm"
              className="min-w-8 px-2"
              onClick={() => onPageChange(p)}
              aria-current={p === page ? "page" : undefined}
            >
              {p}
            </Button>
          ),
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          aria-label={nextLabel}
        >
          {nextLabel}
        </Button>
      </div>
    </div>
  );
}
